<template>
  <loading-wrapper :is-loading="!projectData">
    <styled-subheading>{{ $t('projectSettings.settings') }}</styled-subheading>
    <project-form
      v-if="projectData"
      display-cancel-button
      :mode="ProjectFormMode.EDIT"
      :project-data="transformedProjectData"
      :project-type="ProjectType.SUPPLIER_PROJECT"
      @submit="handleSubmit"
    />
    <projects-docu-managers :project-id="projectData!._id.toString()" />
    <inspectors-list :project-id="projectData!._id.toString()" />
  </loading-wrapper>
</template>

<script setup lang="ts">
  import ProjectForm from '../../components/ProjectForm/ProjectForm.vue';
  import InspectorsList from '../../components/InspectorsList/InspectorsList.vue';
  import ProjectsDocuManagers from '../ProjectsDocuManagers/ProjectsDocuManagers.vue';
  import { IProjectDetailsDto, ProjectErrorCode, ProjectType } from 'types';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ProjectDataService } from '@/services';
  import { useToast } from '@/composables/useToast';
  import {
    ProjectFormFields,
    ProjectFormMode,
  } from '../ProjectForm/ProjectForm.types';
  import { isCustomErrorCodeException } from '@/helpers';

  const emit = defineEmits<{
    (e: 'project-updated'): void;
  }>();
  const props = defineProps<{
    projectData?: IProjectDetailsDto;
  }>();

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();

  const transformedProjectData = computed(
    () =>
      props.projectData &&
      ProjectDataService.transformSupplierProjectDataToFormFields(
        props.projectData,
      ),
  );

  async function handleSubmit(projectForm: ProjectFormFields) {
    try {
      await ProjectDataService.updateSupplierProject(
        props.projectData!._id,
        projectForm,
      );
      createSuccessToast(t('projectForm.projectDataUpdated'));
    } catch (e) {
      if (
        isCustomErrorCodeException(e) &&
        e.response?.data.customErrorCode ===
          ProjectErrorCode.DUPLICATE_EXTERNAL_PROJECT_ID
      ) {
        createErrorToast(t('projectForm.errors.externalIdNotUnique'));
      } else {
        createErrorToast(t('projectForm.errors.errorUpdatingProject'));
      }
    }
    emit('project-updated');
  }
</script>

<style scoped lang="scss">
  h2 {
    margin-top: 34px;
    margin-bottom: 21px;
    font-size: 36px;
    font-weight: 700;
    line-height: 36px;
  }
</style>
